// La pantalla del modo Redes — ADR-2026-10-01-modo-redes.
//
// Una sola pantalla con dos estados:
//
//  • **Portada** (`/redes`): la caja de prompt arriba y las plantillas debajo. Generar
//    CREA la sesión —una fila de `agent_chats` con `tipo = 'redes'`, como un diseño
//    (ADR-2026-09-17-un-diseno-es-un-chat)— y pasa a su URL.
//  • **Sesión** (`/redes/:sesionId`): el lienzo del Agente arriba, con su barra de
//    herramientas en vertical a la derecha, y la caja abajo. Cada Generar cuelga una tanda
//    del lienzo. "Plantillas", abajo a la derecha, esconde el lienzo y vuelve a la vista de
//    la portada sin salir de la sesión.
//
// Que la sesión sea un chat es lo que hace que "Ayuda de prompt" sirva: el panel lateral
// abre la MISMA conversación, así que el agente ve el lienzo, y la pantalla se registra en
// el puente para que lea y escriba el prompt (`describirRedes`).
//
// La generación es la de los modos de origen (`generarEnRedes`); acá sólo se decide a qué
// sesión va y se cuida el saldo y el plan antes de pedir.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, Clapperboard, LayoutGrid, Lock, PanelsTopLeft, Plus } from 'lucide-react';
import { estadoDesdeRedes, proyectoDeRedes } from '../../../../lib/estudioDesdeRedes';
import { recordarGeneracionEnRedes, vistaAlEntrar } from './inactividadDeRedes';
import SubscriptionLimitModal from '../../../../components/SubscriptionLimitModal';
import { ANCHO_LOGO_SIDEBAR_MINIMA, useSidebar } from '../../../../context/SidebarContext';
import { useChatAtlas, useChatAtlasCascaron } from '../../../../chat-atlas/ChatAtlasContext';
import { useRegistrarPantalla, type ControladorDePantalla } from '../../../../chat-atlas';
import { LienzoDelAgente } from '../../../../chat-atlas/lienzo/LienzoDelAgente';
import { useLienzoDelChat } from '../../../../chat-atlas/lienzo/useLienzoDelChat';
import { consultarSaldo, tipoDeLimite, type TipoDeLimite } from '../../../../chat-atlas/lienzo/herramientas/saldo';
import { ModalDeFuentes, type IdDeFuente } from '../../../../chat-atlas/ui/ModalDeFuentes';
import { useAccesoAlAgente } from '../../../../chat-atlas/ui/useAccesoAlAgente';
import { useUserGroup } from '../../../../hooks/useUserGroup';
import { estadoSinMedio, medioDeEntrada } from './entradaACrear';
import { getModePlanLock, type ModePlanLock } from '../../../../lib/planCapabilities';
import { getUserPlan } from '../../../../lib/planUtils';
import { signReferenceImage, uploadReferenceImage, type ChatAttachment } from '../../agentAttachments';
import { VeloSoltarImagen, usePegarYSoltarImagenes } from '../shared/usePegarYSoltarImagenes';
import { conCalidad } from '../creadorVideo/creadorVideo';
import type { ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';
import { medirDuracionDeVideo } from '../../../../utils/medirDuracionDeVideo';
import { TIPO_CHAT_REDES, createChat, leerTituloDeChat, listSesionesRedes, renombrarChat } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import { useUserModels } from '../../modelsContext';
import { useTandasPanel } from '../../useGeneracionesEnCurso';
import { PanelTandaCreaciones } from '../../components/PanelTandaCreaciones';
import type { GeneracionDelegada } from '../../components/generacionDelegada';
import { preloadGeneratedImages, type PreloadedImage } from '../../../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../../../utils/preloadVideosDeTanda';
import { sonarListo } from '../../../../utils/sonarListo';
import { CajaDeRedes } from './CajaDeRedes';
import { FondoDeCeldas } from './FondoDeCeldas';
import { CarruselDePlantillas } from './CarruselDePlantillas';
import { latido, volarHacia } from './volarHacia';
import { PlantillasDeRedes, type PlantillaElegida } from './PlantillasDeRedes';
import { CLAVE_PANTALLA_REDES, aplicarEnRedes, describirRedes } from './describirRedes';
import { deImagenANombre } from './mencionesDeVideo';
import { ajustesConReceta, leerReceta } from './recetaDeRedes';
import { nombreDeMencion } from '../../mencionesDeAdjuntos';
import { MAX_REFERENCIAS_VIDEO } from '../../referenciasDeGeneracion';
import { ordenDeReferencias } from '../promptLibre/promptLibre';
import { escribirPromptEnRedes, pedirEnRedes, type TandaPedida } from './generarEnRedes';
import {
  AJUSTES_DEFAULT,
  RUTA_REDES,
  conAdjunto,
  creditosDeRedes,
  alSumarVideoDeReferencia,
  bloqueoPorVideoDeReferencia,
  faltaEnRedes,
  huellaDeMejora,
  promptYaEscrito,
  rutaDeSesion,
  tituloDeSesion,
  type AjustesDeRedes,
  type HuellaDeMejora,
} from './redes';

const PANTALLA = 'el modo Redes';
/**
 * La pastilla "Ayuda de prompt" (abre el Agente en el panel con el prompt a medias) está
 * OCULTA: con "Mejorar prompt" en la caja, dos botones para lo mismo confundían
 * (ADR-2026-10-04-se-oculta-ayuda-de-prompt). El camino queda entero —la pantalla sigue
 * registrada en el puente y el Agente la lee y la escribe si se lo abre desde la sidebar—;
 * para volver a mostrarla alcanza con este `true`.
 */
const MOSTRAR_AYUDA_DE_PROMPT = false;
/**
 * Las plantillas dentro de Crear —la sección "Explora las plantillas" debajo de la caja y
 * el rótulo "Plantillas" del botón de los resultados, que queda como chevron— están OCULTAS: por ahora las plantillas se
 * prueban sólo como su propio modo, Recrear, que es lo que creemos que un usuario nuevo
 * tiene que ver primero (ADR-2026-10-05-crear-sin-plantillas). Todo sigue armado; para
 * volver a mostrarlas alcanza con este `true`. Sin la sección, en Video no hay dónde anclar
 * un tipo: queda en "general" y el escritor elige el que mejor calce.
 */
const MOSTRAR_PLANTILLAS = false;
const BOTON_ESQUINA =
  'inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white px-4 text-[13.5px] font-semibold text-brand-black shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-colors hover:bg-[#F3F4F8]';

/** Una tanda pedida que todavía no se colgó: espera a que el chat de la sesión esté cargado. */
type TandaEnEspera = TandaPedida & { titulo: string };

/**
 * Cambia de vista (portada ↔ lienzo) con una transición de vista del navegador cuando la
 * hay: la caja viaja de arriba a abajo en vez de saltar (`view-transition-name` en su
 * envoltorio). Sin soporte, el cambio es inmediato, como antes.
 */
function conTransicion(cambiar: () => void) {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => void };
  if (typeof doc.startViewTransition === 'function') {
    doc.startViewTransition(() => flushSync(cambiar));
  } else {
    cambiar();
  }
}

export const PantallaRedes: React.FC<{ sesionId?: string }> = ({ sesionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChatAtlas();
  const { abrirChat } = useChatAtlasCascaron();
  const { companyId, userId, brandId } = useAgentEnv();
  const userModels = useUserModels();
  const { setSidebarMinima, pedirColapsarSidebar, sidebarMinima: tiraDeSidebar } = useSidebar();
  const acceso = useAccesoAlAgente();
  const { isDevTester } = useUserGroup();

  // ── La caja ─────────────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState('');
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const [ajustes, setAjustes] = useState<AjustesDeRedes>(AJUSTES_DEFAULT);
  const [fuentes, setFuentes] = useState<IdDeFuente | null>(null);
  const [generando, setGenerando] = useState(false);
  /** "Mejorar prompt": si está corriendo, y lo último que devolvió el escritor con su
   *  contexto, para no escribir dos veces lo mismo (ADR-2026-10-04-mejorar-prompt-en-redes). */
  const [mejorando, setMejorando] = useState(false);
  const [mejora, setMejora] = useState<HuellaDeMejora | null>(null);
  /** Cómo terminó la última mejora, un momento: el botón dibuja el tilde o la cruz. */
  const [resultadoDeMejora, setResultadoDeMejora] = useState<'listo' | 'fallo' | null>(null);
  useEffect(() => {
    if (!resultadoDeMejora) return;
    const t = window.setTimeout(() => setResultadoDeMejora(null), 1600);
    return () => window.clearTimeout(t);
  }, [resultadoDeMejora]);
  const [error, setError] = useState<string | null>(null);
  /** SÓLO DEV: el motor de imagen forzado desde la pastilla DEV de la caja. No se guarda:
   *  al recargar vuelve a "Atlas elige" (ADR-2026-10-05-forzar-el-modelo-en-redes). */
  const [proveedorForzado, setProveedorForzado] = useState<ProveedorDeImagen | null>(null);
  const [limite, setLimite] = useState<TipoDeLimite | null>(null);
  const [lockVideo, setLockVideo] = useState<ModePlanLock | null>(null);
  /**
   * Qué se ve arriba de la caja: las plantillas (la portada, o "Plantillas" en una
   * sesión) o el lienzo. Es estado propio y no se deriva de la URL para poder animar el
   * cambio antes de navegar a la sesión nueva.
   */
  // Una sesión existente abre en sus creaciones, salvo que haga un rato que no se genera:
  // entonces en la portada (ADR-2026-10-08-redes-abre-colapsado-tras-un-rato). Se decide
  // ya en el primer render para que no se vea la cuadrícula y después salte.
  const [vista, setVista] = useState<'plantillas' | 'lienzo'>(() => {
    if (!sesionId) return 'plantillas';
    const nav = location.state as { verCreaciones?: unknown } | null;
    return nav?.verCreaciones === true ? 'lienzo' : vistaAlEntrar();
  });
  const cambiarVista = useCallback((v: 'plantillas' | 'lienzo') => conTransicion(() => setVista(v)), []);
  const [titulo, setTitulo] = useState<string>('');

  const listo = !!userId && !!companyId;
  const creditos = creditosDeRedes(ajustes);
  // Además de lo de siempre, un video de referencia que no entra en la calidad elegida
  // (se bajó a Básica después de sumarlo) apaga Generar y dice por qué.
  const falta =
    faltaEnRedes({ ajustes, prompt, listo, cantidadAdjuntos: adjuntos.length }) ??
    bloqueoPorVideoDeReferencia(ajustes, adjuntos);

  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  // ── La sesión es un chat (como un diseño) ───────────────────────────────
  const chatActual = chat.motor.getChatId();
  const esElChat = !!sesionId && chatActual === sesionId;
  const cargarChat = chat.cargarChat;
  useEffect(() => {
    if (!sesionId || chat.motor.getChatId() === sesionId) return;
    void cargarChat(sesionId)
      .then(() => {
        if (chat.motor.getChatId() !== sesionId) {
          navigate(RUTA_REDES, { replace: true, state: { aviso: 'Esa sesión no existe o no es de tu empresa.' } });
        }
      })
      .catch(() => {
        navigate(RUTA_REDES, { replace: true, state: { aviso: 'No se pudo abrir la sesión. Intentá de nuevo.' } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);

  useEffect(() => {
    if (!sesionId) {
      setVista('plantillas');
      return;
    }
    // Una sesión recién creada (la primera, o "Nueva sesión") abre en las plantillas: no
    // hay nada en el lienzo todavía. Una existente abre en el lienzo, salvo que haga un
    // rato que no se genera (entonces en la portada, con las creaciones colapsadas). Pedir
    // las creaciones explícitamente ("Volver a creaciones" del Estudio) gana siempre.
    const estadoNav = location.state as { nueva?: unknown; irAPlantillas?: unknown; verCreaciones?: unknown } | null;
    const irAPlantillas = estadoNav?.irAPlantillas === true;
    setVista(
      estadoNav?.nueva || irAPlantillas
        ? 'plantillas'
        : estadoNav?.verCreaciones === true
          ? 'lienzo'
          : vistaAlEntrar(),
    );
    // Volviendo de una plantilla: la vista de plantillas, scrolleada a la sección.
    if (irAPlantillas) {
      window.setTimeout(() => {
        document.getElementById('plantillas-de-redes')?.scrollIntoView({ block: 'start' });
      }, 60);
    }
    void leerTituloDeChat(sesionId).then((t) => {
      if (vivoRef.current && t) setTitulo(t);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);

  // El medio con el que mandaron a abrir: Creador de video → Video, Prompt libre → Imagen
  // (ADR-2026-10-07-prompt-libre-y-creador-abren-crear). Sólo en una sesión: en `/redes`
  // a secas el `replace` hacia la sesión lo arrastra (más abajo) y se aplica acá al llegar.
  // Se aplica y se saca del estado de navegación para que recargar no lo vuelva a poner.
  useEffect(() => {
    if (!sesionId) return;
    const medio = medioDeEntrada(location.state);
    if (!medio) return;
    setAjustes((a) => (a.medio === medio ? a : { ...a, medio }));
    navigate(location.pathname, { replace: true, state: estadoSinMedio(location.state) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, sesionId]);

  // El aviso con el que la sesión mandó de vuelta a la portada.
  useEffect(() => {
    const aviso = (location.state as { aviso?: unknown } | null)?.aviso;
    if (typeof aviso !== 'string') return;
    setError(aviso);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Siempre la misma sesión ──────────────────────────────────────────────
  // `/redes` no es una portada con lista: resuelve a la ÚLTIMA sesión de la empresa y, si
  // no hay ninguna, crea la primera. "Nueva sesión" (el botón de la esquina) es la única
  // forma de abrir otra. Una sesión nueva nace con un título de fecha y toma el prompt de
  // la primera generación como nombre (ver `handleGenerar`).
  const creandoSesionRef = useRef(false);
  const crearSesion = useCallback(async (): Promise<string | null> => {
    if (!userId || !companyId || creandoSesionRef.current) return null;
    creandoSesionRef.current = true;
    try {
      const fecha = new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
      return await createChat({ userId, companyId, title: `Sesión ${fecha}`, messages: [], tipo: TIPO_CHAT_REDES });
    } finally {
      creandoSesionRef.current = false;
    }
  }, [userId, companyId]);
  useEffect(() => {
    if (sesionId || !listo) return;
    let vivo = true;
    void (async () => {
      const [ultima] = await listSesionesRedes();
      if (!vivo) return;
      const irAPlantillas = (location.state as { irAPlantillas?: unknown } | null)?.irAPlantillas === true;
      // El medio pedido (Creador de video → Video) viaja a la sesión con el `replace`,
      // como `irAPlantillas` (ADR-2026-10-07-prompt-libre-y-creador-abren-crear).
      const medio = medioDeEntrada(location.state);
      const estado = { ...(irAPlantillas ? { irAPlantillas: true } : {}), ...(medio ? { medio } : {}) };
      if (ultima) {
        navigate(rutaDeSesion(ultima.id), { replace: true, ...(Object.keys(estado).length ? { state: estado } : {}) });
        return;
      }
      const id = await crearSesion();
      if (!vivo) return;
      if (id) navigate(rutaDeSesion(id), { replace: true, state: { nueva: true, ...(medio ? { medio } : {}) } });
      else setError('No se pudo abrir una sesión. Recargá la página.');
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId, listo]);
  const nuevaSesion = useCallback(async () => {
    const id = await crearSesion();
    if (!vivoRef.current) return;
    if (id) {
      setTitulo('');
      navigate(rutaDeSesion(id), { state: { nueva: true } });
    } else setError('No se pudo crear la sesión.');
  }, [crearSesion, navigate]);

  // La sidebar a la tira desde que se entra (portada y sesión), como el Agente.
  useEffect(() => {
    setSidebarMinima(true);
    pedirColapsarSidebar();
    return () => setSidebarMinima(false);
  }, [setSidebarMinima, pedirColapsarSidebar]);

  // El panel del agente se va con la sesión: si quedó abierto sobre ESTE chat, se cierra
  // al salir (misma regla que `hayQueCerrarElPanelAlSalir` de Diseño).
  const alSalirRef = useRef<() => void>(() => {});
  alSalirRef.current = () => {
    if (sesionId && chat.abierto && chat.vista === 'panel' && chat.motor.getChatId() === sesionId) chat.cerrarChat();
  };
  useEffect(() => () => alSalirRef.current(), []);

  // ── Los resultados: la grilla del Creador o el canva ─────────────────────
  // La MISMA grilla que el Creador de video (`PanelTandaCreaciones` con el registro de
  // tandas en curso): todas las imágenes y videos de la empresa, con los recuadros de lo
  // que está generando arriba. "Canva" es el lienzo del Agente. Una tanda se registra en
  // los DOS lados con la misma promesa; cada vista dibuja la suya.
  const { tandasPanel, recargarToken, crearTanda, cerrarTanda, fallarTanda } = useTandasPanel(companyId, 'redes');
  const [modoDeVista, setModoDeVista] = useState<'cuadricula' | 'canva'>('cuadricula');
  // El último proyecto del Estudio abierto desde esta sesión: con él, al lado de
  // Cuadrícula | Canva aparece "Estudio de video" (ADR-2026-10-05-el-estudio-desde-redes-queda-a-mano).
  // Se lee al montar: volver del editor vuelve a montar la pantalla.
  const proyectoDelEstudio = useMemo(() => proyectoDeRedes(sesionId), [sesionId]);
  const registrarEnPanel = useCallback(
    (t: { total: number; ratio: number; esVideo: boolean; executionId?: string; duracionEstimadaMs?: number; resultado: Promise<string[]> }) => {
      if (!companyId) return;
      // Se generó en Redes (desde la caja o desde el visor): cuenta para abrir en las
      // creaciones la próxima vez (ADR-2026-10-08-redes-abre-colapsado-tras-un-rato).
      recordarGeneracionEnRedes();
      const id = crearTanda({
        total: t.total,
        ratio: t.ratio,
        modo: 'redes',
        esVideo: t.esVideo,
        ...(t.executionId ? { executionIds: [t.executionId] } : {}),
        ...(t.duracionEstimadaMs ? { duracionEstimadaMs: t.duracionEstimadaMs } : {}),
      });
      if (!id) return;
      t.resultado
        .then(async (ids) => {
          let pre: PreloadedImage[] = [];
          try {
            pre = t.esVideo ? await preloadVideosDeTanda(ids, companyId) : await preloadGeneratedImages(ids, companyId);
          } catch {
            /* sin preload cierra vacía y la grilla se recarga */
          }
          cerrarTanda(id, pre, { esVideo: t.esVideo });
          sonarListo();
        })
        .catch(() => fallarTanda(id));
    },
    [companyId, crearTanda, cerrarTanda, fallarTanda],
  );
  // Carrusel / Animar pedidos desde el visor de la grilla aterrizan como una tanda más.
  const alDelegar = useCallback(
    (g: GeneracionDelegada) => {
      registrarEnPanel({ total: g.total, ratio: g.ratio, esVideo: g.esVideo, executionId: g.executionIds?.[0], duracionEstimadaMs: g.duracionEstimadaMs, resultado: g.resultado });
    },
    [registrarEnPanel],
  );
  // Cuánto mide la caja que flota abajo, para que la grilla pueda scrollear por debajo
  // hasta el final (como `altoCaja` en el Creador).
  const cajaRef = useRef<HTMLDivElement | null>(null);
  const [altoCaja, setAltoCaja] = useState(260);
  useEffect(() => {
    const nodo = cajaRef.current;
    if (!nodo || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => {
      const alto = e?.borderBoxSize?.[0]?.blockSize ?? nodo.offsetHeight;
      if (alto > 0) setAltoCaja(alto);
    });
    ro.observe(nodo);
    return () => ro.disconnect();
  }, [sesionId, vista]);

  // ── El lienzo ───────────────────────────────────────────────────────────
  const estado = useLienzoDelChat();
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(0);
  const lienzoVisible = !!sesionId && vista === 'lienzo';
  useEffect(() => {
    const el = contenedorRef.current;
    if (!el || !lienzoVisible || typeof ResizeObserver === 'undefined') return;
    const medir = () => setAncho(el.clientWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [lienzoVisible]);

  const colgar = useCallback(
    (t: TandaEnEspera) => {
      registrarEnPanel(t);
      estado.agregarTanda({
        titulo: t.titulo,
        total: t.total,
        ratio: t.ratio,
        esVideo: t.esVideo,
        executionId: t.executionId,
        resultado: t.resultado,
      });
      t.resultado.catch((e: unknown) => {
        if (vivoRef.current) setError(e instanceof Error && e.message ? e.message : 'No se pudo generar. Intentá nuevamente.');
      });
    },
    [estado, registrarEnPanel],
  );

  // Una tanda pedida desde la portada espera a que el chat de la sesión nueva esté cargado.
  const enEsperaRef = useRef<TandaEnEspera | null>(null);
  useEffect(() => {
    if (!esElChat || !enEsperaRef.current) return;
    const t = enEsperaRef.current;
    enEsperaRef.current = null;
    colgar(t);
  }, [esElChat, colgar]);

  // ── Generar ─────────────────────────────────────────────────────────────
  const handleGenerar = async () => {
    if (falta || generando || !userId || !companyId) return;
    setGenerando(true);
    try {
      // Video pide Pro/Ultra, el mismo piso del Creador. El plan se vuelve a leer acá
      // porque entrar al modo no lo pide.
      if (ajustes.medio === 'video' && !isDevTester) {
        const lock = getModePlanLock('creador-video', await getUserPlan());
        if (lock) {
          setLockVideo(lock);
          return;
        }
      }
      const saldo = await consultarSaldo(companyId);
      if (!vivoRef.current) return;
      if (saldo.restantes !== null && saldo.restantes < creditos) {
        setLimite(tipoDeLimite(saldo.plan));
        return;
      }
      const pedido = pedirEnRedes({
        userId,
        companyId,
        prompt,
        adjuntos,
        ajustes,
        modeloUrl: (id) => userModels.find((m) => String(m.id) === id)?.thumbnailUrl ?? null,
        // Si el prompt es el que devolvió "Mejorar prompt" (igual o casi, mismo contexto),
        // el Creador no lo vuelve a escribir.
        promptEscrito: promptYaEscrito(mejora, prompt, ajustes, adjuntos),
        // Imagen: lo que la mejora ya declaró (motor, fondo blanco, etiquetas) viaja con
        // el prompt, así no se decide otra vez.
        declarado: promptYaEscrito(mejora, prompt, ajustes, adjuntos) ? mejora?.declarado : undefined,
        // Sólo cuentas dev: el motor forzado desde la pastilla DEV.
        ...(isDevTester && proveedorForzado ? { proveedorForzado } : {}),
      });
      const tanda: TandaEnEspera = { ...pedido, titulo: tituloDeSesion(prompt, ajustes.medio) };
      cambiarVista('lienzo');
      if (sesionId) {
        // La primera generación le pone nombre a la sesión (nació con la fecha).
        if (estado.tandas.length === 0) {
          void renombrarChat(sesionId, tanda.titulo).then((ok) => {
            if (ok && vivoRef.current) setTitulo(tanda.titulo);
          });
        }
        if (esElChat) colgar(tanda);
        else enEsperaRef.current = tanda;
        return;
      }
      // Sin sesión: se crea y se navega. La tanda ya está en vuelo; se cuelga apenas el
      // chat nuevo sea el actual (`enEsperaRef`).
      enEsperaRef.current = tanda;
      const id = await createChat({ userId, companyId, title: tanda.titulo, messages: [], tipo: TIPO_CHAT_REDES });
      if (!vivoRef.current) return;
      if (!id) {
        enEsperaRef.current = null;
        setError('No se pudo crear la sesión. El pedido igual salió: lo vas a encontrar en Creaciones.');
        return;
      }
      setTitulo(tanda.titulo);
      navigate(rutaDeSesion(id));
    } finally {
      if (vivoRef.current) setGenerando(false);
    }
  };

  // ── Adjuntos (el selector del Agente, en la pestaña que corresponda) ────
  const alAdjuntar = useCallback((a: ChatAttachment) => setAdjuntos((prev) => conAdjunto(prev, a)), []);
  const quitarAdjunto = useCallback((id: string) => setAdjuntos((prev) => prev.filter((a) => a.id !== id)), []);

  // ── Pegar (Ctrl/Cmd+V) o soltar una imagen: entra como referencia, igual que subirla
  //    desde "Añadir referencia" (ADR-2026-10-04-pegar-imagenes-en-la-caja-de-redes). El
  //    mismo hook de Prompt libre y el Creador, acotado a esta pantalla: el panel del
  //    Agente tiene su propio pegado. Un pegado de texto no se toca.
  const raizRef = useRef<HTMLDivElement | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const subirImagenes = useCallback(
    async (archivos: File[]) => {
      if (!companyId || archivos.length === 0) return;
      setSubiendo(true);
      let fallo: string | null = null;
      // De a una y en orden: el "subiendo" de la caja refleja la que está en vuelo.
      for (const archivo of archivos) {
        try {
          const { storagePath, previewUrl } = await uploadReferenceImage(archivo, companyId);
          if (!vivoRef.current) return;
          alAdjuntar({ kind: 'image', id: `img-${crypto.randomUUID()}`, storagePath, previewUrl });
        } catch (e) {
          fallo = fallo ?? (e instanceof Error && e.message ? e.message : 'No se pudo subir esa imagen.');
        }
      }
      if (!vivoRef.current) return;
      setSubiendo(false);
      if (fallo) setError(fallo);
    },
    [companyId, alAdjuntar],
  );
  // ── Una creación pasa a ser referencia de la caja, volando ──────────────
  //    La foto se achica y vuela a la tarjeta "Añadir referencia"; al llegar entra como
  //    adjunto (ahí aparece su miniatura). Lo usan "Editar" de la cuadrícula (que además
  //    escribe el prompt, `alLlegar`) y "Agregar" del lienzo
  //    (ADR-2026-10-03-redes-editar-vuela-a-la-referencia,
  //    ADR-2026-10-04-agregar-del-lienzo-va-a-la-caja-de-redes). Sin origen o sin destino
  //    a la vista, entra directo.
  const volarAReferencia = useCallback(
    (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }, alLlegar?: () => void) => {
      const destino = document.querySelector('[data-destino-referencia]');
      const sumar = () => {
        setAdjuntos((prev) => conAdjunto(prev, adjunto));
        alLlegar?.();
        latido(destino);
      };
      if (!origen || !destino) {
        sumar();
        return;
      }
      const r = destino.getBoundingClientRect();
      // Aterriza sobre el ícono de la tarjeta (42 px, a la izquierda).
      const blanco = new DOMRect(r.left + 12, r.top + (r.height - 42) / 2, 42, 42);
      void volarHacia(origen.src, origen.rect, blanco).then(sumar);
    },
    [],
  );
  // Un VIDEO como referencia tiene sus reglas (ADR-2026-10-05-bloqueos-del-video-de-referencia):
  // sólo sirve para generar video (la caja pasa a Video), es uno solo (`conAdjunto` reemplaza
  // al anterior) y su largo tiene tope según la calidad: si no entra en Básica se pasa a
  // Profesional avisando, y si no entra ni ahí no se suma. Devuelve si se sumó.
  const calidadDeVideoRef = useRef(ajustes.video.ajustes.quality);
  calidadDeVideoRef.current = ajustes.video.ajustes.quality;
  const sumarVideoDeReferencia = useCallback(
    async (crudo: ChatAttachment, origen?: { rect: DOMRect; src: string }) => {
      if (crudo.kind !== 'video') return;
      // Si la fila no decía cuánto dura (Animar imagen, plantillas, Corregir voz), se mide
      // del archivo: sin el dato no hay tope que cuidar.
      let adjunto = crudo;
      if (!(crudo.duracionSeg > 0)) {
        const url = await signReferenceImage(crudo.storagePath, crudo.bucket).catch(() => null);
        const medido = url ? await medirDuracionDeVideo(url) : 0;
        if (!vivoRef.current) return;
        if (medido > 0) adjunto = { ...crudo, duracionSeg: Math.round(medido * 10) / 10 };
      }
      const r = alSumarVideoDeReferencia(adjunto.duracionSeg, calidadDeVideoRef.current);
      if (r.accion === 'bloquear') {
        setError(r.motivo);
        return;
      }
      setAjustes((a) => ({
        ...a,
        medio: 'video',
        video: r.accion === 'subir' ? { ...a.video, ajustes: conCalidad(a.video.ajustes, 'profesional') } : a.video,
      }));
      if (r.accion === 'subir') setError(r.aviso);
      volarAReferencia(adjunto, origen);
    },
    [volarAReferencia],
  );
  // "Agregar" en el lienzo: la foto NO va al chat, va de referencia a la caja.
  const agregarDelLienzo = useMemo(
    () => ({
      rotulo: 'Agregar',
      agregar: (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }) =>
        adjunto.kind === 'video' ? void sumarVideoDeReferencia(adjunto, origen) : volarAReferencia(adjunto, origen),
    }),
    [volarAReferencia, sumarVideoDeReferencia],
  );

  // ── Rehacer: lo que generó una creación vuelve a la caja ────────────────
  //    (ADR-2026-10-04-rehacer-desde-la-cuadricula). El prompt es el que el usuario mandó
  //    (no el del escritor), con sus adjuntos y los ajustes de ese medio. La huella de
  //    "Mejorar prompt" se borra: ese texto no salió de una mejora de ahora.
  const rehacer = useCallback(async (datosGeneracion: unknown) => {
    const receta = leerReceta(datosGeneracion);
    if (!receta) return;
    setAjustes((a) => ajustesConReceta(a, receta));
    setPrompt(receta.prompt);
    setMejora(null);
    setAdjuntos(receta.adjuntos);
    // Las imágenes subidas guardaron una vista previa FIRMADA, que vence: se vuelve a
    // firmar. La referencia que viaja al generar se firma aparte, por su path.
    const ids = receta.adjuntos.map((a) => a.id).join(',');
    const frescos = await Promise.all(
      receta.adjuntos.map(async (a) => {
        if (a.kind !== 'image') return a;
        try {
          const url = await signReferenceImage(a.storagePath, a.bucket);
          return url ? { ...a, previewUrl: url } : a;
        } catch {
          return a;
        }
      }),
    );
    if (!vivoRef.current) return;
    // Sólo si la caja sigue con esos mismos adjuntos: si el usuario ya tocó algo, manda él.
    setAdjuntos((prev) => (prev.map((a) => a.id).join(',') === ids ? frescos : prev));
  }, []);

  const { arrastrando } = usePegarYSoltarImagenes({
    // Con el selector de fuentes o un aviso abierto, la foto no tiene que caer atrás.
    activo: listo && fuentes === null && !limite && !lockVideo,
    onImagenes: (archivos) => void subirImagenes(archivos),
    dentroDe: raizRef,
  });

  // ── Mejorar prompt: el escritor del Creador, suelto, y su texto a la caja ──
  const mejorarPrompt = useCallback(async () => {
    if (mejorando || generando || !userId || !companyId) return;
    setMejorando(true);
    setResultadoDeMejora(null);
    try {
      const escrito = await escribirPromptEnRedes({
        userId,
        companyId,
        prompt,
        adjuntos,
        ajustes,
        modeloUrl: (id) => userModels.find((m) => String(m.id) === id)?.thumbnailUrl ?? null,
      });
      if (!vivoRef.current) return;
      setPrompt(escrito.prompt);
      setMejora(huellaDeMejora(escrito.prompt, ajustes, adjuntos, escrito.declarado));
      setResultadoDeMejora('listo');
    } catch (e) {
      if (vivoRef.current) {
        setResultadoDeMejora('fallo');
        setError(e instanceof Error && e.message ? e.message : 'No pudimos mejorar el prompt. Probá de nuevo.');
      }
    } finally {
      if (vivoRef.current) setMejorando(false);
    }
  }, [mejorando, generando, userId, companyId, ajustes, prompt, adjuntos, userModels]);

  // ── Ayuda de prompt: el agente en el panel, con el prompt a medias precargado ─
  const ayudaDePrompt = useCallback(() => {
    const medio = ajustes.medio === 'video' ? 'un video' : 'una imagen';
    const texto = prompt.trim();
    abrirChat({
      contexto: { pantalla: PANTALLA, ruta: location.pathname, detalle: titulo ? `la sesión «${titulo}»` : undefined },
      vista: 'panel',
      mensajeInicial: texto
        ? `Ayudame a mejorar este prompt para ${medio} para redes: «${texto}»`
        : `Ayudame a escribir el prompt para ${medio} para redes. Quiero `,
    });
  }, [abrirChat, ajustes.medio, prompt, location.pathname, titulo]);

  // ── Plantillas de imagen: el flujo de plantilla de siempre ──────────────
  const elegirPlantilla = useCallback(
    (p: PlantillaElegida) => {
      navigate(`/interfaz-v2?preset=${p.preset.id}`, {
        state: {
          presetId: p.preset.id,
          collectionId: p.collectionId,
          presetData: p.preset,
          collectionPresets: p.collectionPresets,
          saltearPreview: true,
          // Las migas de la plantilla vuelven acá ("Plantillas" → Redes, en su sección).
          desdeRedes: true,
        },
      });
    },
    [navigate],
  );

  // ── El puente con el agente ─────────────────────────────────────────────
  const nombresDeAdjuntos = useMemo(
    () => adjuntos.map((a) => (a.kind === 'image' ? 'una imagen subida' : `${a.kind === 'product' ? 'Producto' : a.kind === 'model' ? 'Modelo' : 'Referencia'}: ${a.name}`)),
    [adjuntos],
  );
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: CLAVE_PANTALLA_REDES,
        describir: () =>
          describirRedes({
            ruta: location.pathname,
            prompt,
            ajustes,
            cantidadAdjuntos: adjuntos.length,
            adjuntos: nombresDeAdjuntos,
            menciones: adjuntos.map((a) => `@${nombreDeMencion(a, adjuntos)}`),
            enSesion: !!sesionId,
          }),
        aplicar: (cambios) =>
          aplicarEnRedes(cambios, {
            // Si el agente igual escribió `@Image1` (es como redacta video), en la caja se
            // ve con el nombre del adjunto que ocuparía esa posición
            // (ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre).
            setPrompt: (v) =>
              setPrompt(
                ajustes.medio === 'video'
                  ? deImagenANombre(v, ordenDeReferencias(adjuntos, MAX_REFERENCIAS_VIDEO), adjuntos)
                  : v,
              ),
            setFormato: (slug) =>
              setAjustes((a) => (a.medio === 'video' ? { ...a, video: { ...a.video, formatSlug: slug } } : { ...a, imagen: { ...a.imagen, formatSlug: slug } })),
            setCalidadImagen: (v) => setAjustes((a) => ({ ...a, imagen: { ...a.imagen, calidad: v } })),
            setCantidad: (n) => setAjustes((a) => ({ ...a, imagen: { ...a.imagen, cantidad: n } })),
            setDuracion: (s) => setAjustes((a) => ({ ...a, video: { ...a.video, ajustes: { ...a.video.ajustes, durationSeconds: s } } })),
          }),
      }),
      [location.pathname, prompt, ajustes, adjuntos, nombresDeAdjuntos, sesionId],
    ),
  );

  // ── Render ──────────────────────────────────────────────────────────────
  const caja = (
    <CajaDeRedes
      prompt={prompt}
      onPromptChange={setPrompt}
      onGenerar={() => void handleGenerar()}
      falta={falta}
      generando={generando}
      adjuntos={adjuntos}
      onQuitarAdjunto={quitarAdjunto}
      onAbrirFuentes={setFuentes}
      ajustes={ajustes}
      onAjustes={setAjustes}
      onAyudaDePrompt={MOSTRAR_AYUDA_DE_PROMPT && acceso === 'permitido' ? ayudaDePrompt : undefined}
      onMejorarPrompt={() => void mejorarPrompt()}
      mejorando={mejorando}
      resultadoDeMejora={resultadoDeMejora}
      subiendo={subiendo}
      // La pastilla DEV sólo existe para `dev_tester`.
      proveedorForzado={proveedorForzado}
      onProveedorForzado={isDevTester ? setProveedorForzado : undefined}
    />
  );

  const plantillas = (
    <PlantillasDeRedes
      medio={ajustes.medio}
      tipoVideo={ajustes.video.tipoVideo}
      onTipoVideo={(slug) => setAjustes((a) => ({ ...a, video: { ...a.video, tipoVideo: slug } }))}
      onElegirPlantilla={elegirPlantilla}
    />
  );

  return (
    <div ref={raizRef} className="relative flex h-[calc(100dvh-env(safe-area-inset-top)-4.25rem)] flex-col overflow-hidden md:h-[100dvh]">
      <VeloSoltarImagen visible={arrastrando} />
      {/* Sin encabezado: la pantalla es la caja y el lienzo. En la esquina (con la sidebar
          en tira, al lado de su logo) sólo "Nueva sesión", y sólo en el CANVA: la sesión es
          lo que ese lienzo junta; la cuadrícula muestra todo lo de la empresa y las
          plantillas no dependen de la sesión, así que ahí no tiene sentido ofrecerla. */}
      {sesionId && lienzoVisible && modoDeVista === 'canva' && (
        <button
          type="button"
          onClick={() => void nuevaSesion()}
          style={{ left: (tiraDeSidebar ? ANCHO_LOGO_SIDEBAR_MINIMA : 0) + 16 }}
          className={`${BOTON_ESQUINA} absolute top-4 z-20 transition-[left] duration-300 ease-out`}
        >
          <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          Nueva sesión
        </button>
      )}

      {!sesionId ? null : lienzoVisible ? (
        <>
          {/* El lienzo ocupa TODO el alto y la caja flota encima, abajo: el canvas pasa
              por debajo de ella hasta el borde, como la grilla del Creador pasa por debajo
              de su caja (ADR-2026-09-08-el-chrome-del-creador-de-video-flota-sobre-la-grilla). */}
          <div ref={contenedorRef} className="relative min-h-0 flex-1">
            {modoDeVista === 'canva' ? (
              <div className="absolute inset-0 isolate z-0">
                {esElChat && ancho > 0 && <LienzoDelAgente estado={estado} ancho={ancho} barra="derecha" alAgregar={agregarDelLienzo} />}
              </div>
            ) : (
              // La grilla scrollea por debajo del toggle (arriba) y de la caja (abajo).
              // `isolate`: las cards traen botones con `z-index` propio (descargar, etc.)
              // y sin un contexto de apilado acá se dibujaban POR ENCIMA de la caja.
              <div className="absolute inset-0 isolate z-0 overflow-y-auto px-6" style={{ paddingTop: 72, paddingBottom: altoCaja + 28 }}>
                <PanelTandaCreaciones
                  tandas={tandasPanel}
                  companyId={companyId}
                  brandId={brandId ?? null}
                  recargarToken={recargarToken}
                  modoActual="redes"
                  // Cinco columnas fijas: a pantalla completa `auto` repartía en seis o
                  // siete cards chicas; cinco es lo que se pidió para el ancho entero.
                  columnas={5}
                  filtro="todo"
                  onGeneracionDelegada={alDelegar}
                  // La card limpia: rótulo y acciones al pasar el mouse. "Editar" pone la
                  // creación de referencia en la caja con un prompt que lo dice.
                  accionesEnHover={{
                    // "Rehacer" sólo en las creaciones que guardaron con qué se hicieron.
                    puedeRehacer: (d) => leerReceta(d) !== null,
                    onRehacer: (d) => void rehacer(d),
                    // La foto se achica y vuela a "Añadir referencia"; al llegar entra como
                    // adjunto (ahí aparece su miniatura) y se escribe el prompt.
                    onEditar: (adjunto, origen) => {
                      if (adjunto.kind === 'video') {
                        // "Añadir como referencia" de un VIDEO: pasa a Video, respeta el
                        // tope de duración y es uno solo. No se escribe prompt: no es una
                        // edición, es estilo y ritmo.
                        void sumarVideoDeReferencia(adjunto, origen);
                        return;
                      }
                      volarAReferencia(adjunto, origen, () => setPrompt('Quisiera editar esta imagen'));
                    },
                  }}
                />
              </div>
            )}
            {/* Cuadrícula | Canva: cómo se ve lo generado. */}
            <div
              role="tablist"
              aria-label="Vista de los resultados"
              className="absolute right-6 top-4 z-20 inline-flex h-10 items-center rounded-full bg-white p-1 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05]"
            >
              {([
                { id: 'cuadricula', rotulo: 'Cuadrícula', Icono: LayoutGrid },
                { id: 'canva', rotulo: 'Canva', Icono: PanelsTopLeft },
              ] as const).map(({ id, rotulo, Icono }) => {
                const activa = modoDeVista === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={activa}
                    onClick={() => setModoDeVista(id)}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-colors ${
                      activa ? 'bg-[#EEF0F5] text-brand-black' : 'text-[#02062B80] hover:text-brand-black'
                    }`}
                  >
                    <Icono className={`h-4 w-4 ${activa ? 'text-brand-primary' : ''}`} strokeWidth={2} aria-hidden="true" />
                    {rotulo}
                  </button>
                );
              })}
              {/* No es una vista: lleva al último proyecto del Estudio abierto desde esta
                  sesión, y el editor ofrece volver acá. */}
              {proyectoDelEstudio && (
                <button
                  type="button"
                  onClick={() => navigate(`/editor-video/${proyectoDelEstudio}`, { state: estadoDesdeRedes(location.pathname) })}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-[#02062B80] transition-colors hover:text-brand-black"
                >
                  <Clapperboard className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  Estudio de video
                </button>
              )}
            </div>
            {/* "Plantillas", en la esquina inferior derecha: esconde los resultados y vuelve
                a la vista de la portada sin salir de la sesión. */}
            {/* Sólo un chevron, sin rótulo: con las plantillas ocultas
                (ADR-2026-10-05-crear-sin-plantillas) ya no lleva a ellas, sólo vuelve a la
                portada de la sesión (ADR-2026-10-05-el-boton-de-portada-es-un-chevron). */}
            <button
              type="button"
              onClick={() => cambiarVista('plantillas')}
              aria-label="Volver a la portada"
              title="Volver a la portada"
              className="absolute bottom-6 right-6 z-20 grid h-10 w-10 place-items-center rounded-full bg-white text-brand-black shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-colors hover:bg-[#F3F4F8]"
            >
              <ChevronDown className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            </button>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-6 pb-5">
              <div ref={cajaRef} className="pointer-events-auto mx-auto max-w-[1100px]" style={{ viewTransitionName: 'caja-de-redes' }}>
                {caja}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="relative min-h-0 flex-1">
          <div className={`h-full overflow-y-auto px-6 ${MOSTRAR_PLANTILLAS ? 'pb-16' : ''}`}>
            {/* La primera pantalla: la frase y la caja a MEDIA altura, centradas; las
                plantillas arrancan debajo del pliegue y se llega scrolleando. */}
            {/* `-mx-6 px-6`: el fondo de celdas llega a los bordes del scroller; el
                contenido conserva su aire. */}
            {/* Sin plantillas debajo, la portada ocupa toda la altura (nada asoma bajo el pliegue). */}
            <div
              className={`relative -mx-6 flex flex-col justify-center px-6 py-10 ${
                MOSTRAR_PLANTILLAS ? 'min-h-[calc(100%-6rem)]' : 'min-h-full'
              }`}
            >
              <FondoDeCeldas />
              <h2 className="relative z-10 mb-6 text-center text-[34px] font-bold leading-[1.15] tracking-[-0.02em] text-brand-black">
                ¿Qué publicamos hoy?
              </h2>
              {/* Entre el título y la caja, el abanico de plantillas que va pasando solo; con
                  Video pasa a los clips (el cambio de medio lo anima el carrusel). */}
              <CarruselDePlantillas medio={ajustes.medio} className="relative z-10 mb-6" />
              <div className="relative z-10 mx-auto w-full max-w-[1100px]" style={{ viewTransitionName: 'caja-de-redes' }}>
                {caja}
              </div>
            </div>
            {MOSTRAR_PLANTILLAS && <div>{plantillas}</div>}
          </div>
          {/* "Ver creaciones", arriba a la derecha (donde en los resultados va el toggle
              Cuadrícula | Canva): pasa a los resultados con la transición de vista —la caja
              viaja de su lugar al pie— y abre en la vista que se usó la última vez. */}
          {sesionId && (
            <button
              type="button"
              onClick={() => cambiarVista('lienzo')}
              className={`${BOTON_ESQUINA} absolute right-6 top-4 z-20 transform transition-transform duration-300 hover:-translate-y-0.5`}
            >
              <LayoutGrid className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              Ver creaciones
            </button>
          )}
        </div>
      )}

      {/* El selector del Agente, en la pestaña que pidió el botón. `onAdjuntar` lo saca
          del chat: lo elegido entra a ESTA caja (ADR-2026-09-27-editar-clip-agrega-con-el-selector-del-agente). */}
      <ModalDeFuentes abierto={fuentes !== null} onCerrar={() => setFuentes(null)} onAdjuntar={alAdjuntar} multiple adjuntosActuales={adjuntos} onQuitar={quitarAdjunto} pestanaInicial={fuentes ?? 'productos'} />

      <SubscriptionLimitModal isOpen={limite !== null} onClose={() => setLimite(null)} type={limite ?? 'plan-limit-reached'} />

      {lockVideo &&
        createPortal(
          <div className="fixed inset-0 z-[140] flex items-center justify-center p-6" role="dialog" aria-modal="true" aria-label={lockVideo.title}>
            <button type="button" aria-label="Cerrar" onClick={() => setLockVideo(null)} className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]" />
            <div className="animate-fade-in relative z-10 w-full max-w-[420px] rounded-2xl bg-white p-6 text-center shadow-[0_24px_80px_rgba(16,24,45,0.28)]">
              <Lock className="mx-auto h-7 w-7 text-[#02062B]/[0.4]" aria-hidden="true" />
              <p className="mt-3 text-[16px] font-semibold text-brand-black">{lockVideo.title}</p>
              <p className="mt-1.5 text-[14px] text-[#3C3C43]/60">{lockVideo.message}</p>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="mt-5 rounded-full bg-brand-primary py-2.5 px-5 text-[14.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
              >
                Ver planes
              </button>
            </div>
          </div>,
          document.body,
        )}

      {error &&
        createPortal(
          <button type="button" onClick={() => setError(null)} className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4">
            <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-6 text-sm text-white shadow-xl">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
              {error}
            </span>
          </button>,
          document.body,
        )}
    </div>
  );
};

export default PantallaRedes;
